import { useQuery } from "@apollo/client/react";
import differenceWith from "lodash-es/differenceWith";
import { useMemo } from "react";

import { BaseSelectItem } from "ds/components/Select/types";
import { ExternalIntegration } from "types/generated";

import { GET_ATTACHABLE_INTEGRATIONS_IN_SPACE } from "./gql";

type AttachableIntegrationOption = BaseSelectItem & {
  value: ExternalIntegration["name"];
};

type UseGetAttachableIntegrationsProps = {
  spaceId: string;
  attachedIntegrationsIds: string[];
  skip?: boolean;
};

type UseGetAttachableIntegrationsData = {
  integrations: AttachableIntegrationOption[];
  loading: boolean;
};

const useGetAttachableIntegrations = ({
  spaceId,
  attachedIntegrationsIds,
}: UseGetAttachableIntegrationsProps): UseGetAttachableIntegrationsData => {
  const { data, loading } = useQuery<{
    attachableExternalIntegrationInSpace: ExternalIntegration[];
  }>(GET_ATTACHABLE_INTEGRATIONS_IN_SPACE, {
    variables: { id: spaceId },
  });

  const integrations = useMemo(
    () =>
      differenceWith(
        (data?.attachableExternalIntegrationInSpace || []).map((integration) => ({
          value: integration.id,
          label: integration.name,
        })),
        attachedIntegrationsIds,
        (a, b) => a.value === b
      ),
    [data?.attachableExternalIntegrationInSpace, attachedIntegrationsIds]
  );

  return {
    integrations,
    loading,
  };
};

export default useGetAttachableIntegrations;
